// Create new file: components/QuickActionsPanel.js

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  CloudDownload,
  Storage,
  AdminPanelSettings,
  OpenInNew,
  SwapHoriz,
  ContentCopy,
  CheckCircle,
} from "@mui/icons-material";
import {
  downloadCompleteSite,
  getPhpMyAdminLogin,
  getWpAdminLogin,
} from "../redux/slices/siteSlice";
import { migrateSite } from "../redux/slices/migrationSlice";
import { onMigrationProgress, offMigrationProgress } from "../services/socket";

const MIGRATION_STEPS = [
  { key: "backup", label: "Creating Backup" },
  { key: "cloning", label: "Initializing Clone" },
  { key: "creating_containers", label: "Creating Containers" },
  { key: "waiting_containers", label: "Starting Containers" },
  { key: "waiting_mysql", label: "Waiting for Database" },
  { key: "installing_wpcli", label: "Installing Tools" },
  { key: "copying_files", label: "Copying Files" },
  { key: "cloning_database", label: "Cloning Database" },
  { key: "updating_config", label: "Updating Config" },
  { key: "updating_urls", label: "Updating URLs" },
  { key: "finalizing", label: "Finalizing" },
  { key: "completed", label: "Completed" },
];

export const QuickActionsPanel = ({ site, siteId }) => {
  const dispatch = useDispatch();
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [targetEnvironment, setTargetEnvironment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [migrationProgress, setMigrationProgress] = useState(null);

  const [pmaLoading, setPmaLoading] = useState(false);
  const [wpLoading, setWpLoading] = useState(false);

  const { downloadLoading } = useSelector((state) => state.site);
  const { migrateLoading } = useSelector((state) => state.migration);

  // Listen for real-time migration progress
  const handleProgress = useCallback(
    (data) => {
      if (data.sourceSiteId === siteId || data.siteId === siteId) {
        setMigrationProgress(data);
      }
    },
    [siteId],
  );

  useEffect(() => {
    onMigrationProgress(handleProgress);
    return () => offMigrationProgress(handleProgress);
  }, [handleProgress]);

  const handleDownloadSite = async () => {
    try {
      await dispatch(downloadCompleteSite(siteId)).unwrap();
      setSuccessMessage("Site download started successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      const errorMsg =
        typeof error === "string"
          ? error
          : error?.error ||
            error?.message ||
            "Failed to download site. Try using Backup & Download instead.";
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleMigration = async () => {
    if (!targetEnvironment) return;

    setMigrationProgress(null);
    try {
      await dispatch(
        migrateSite({
          siteId,
          payload: {
            targetEnvironment,
            createBackup: true,
            rollbackOnFailure: true,
          },
        }),
      ).unwrap();

      setSuccessMessage(
        `Migration to ${targetEnvironment} started successfully! Check the Deployments tab to track progress.`,
      );
      setTimeout(() => {
        setMigrationOpen(false);
        setTargetEnvironment("");
        setMigrationProgress(null);
      }, 2000);
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.error || error?.message || "Failed to start migration";
      setErrorMessage(msg);
      setMigrationProgress(null);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handlePhpMyAdmin = async () => {
    try {
      setPmaLoading(true);
      const result = await dispatch(getPhpMyAdminLogin(siteId)).unwrap();

      // Open PHPMyAdmin - it will automatically connect to the site's database
      window.open(result.url, "_blank");

      setSuccessMessage(
        "PHPMyAdmin opened with automatic database connection!",
      );
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage("Failed to open PHPMyAdmin");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setPmaLoading(false);
    }
  };

  const handleWpAdmin = async () => {
    try {
      setWpLoading(true);
      const result = await dispatch(getWpAdminLogin(siteId)).unwrap();
      // Use adminUrl to go directly to wp-admin, loginUrl for auto-login
      window.open(result.loginUrl || result.adminUrl, "_blank");
    } catch (error) {
      setErrorMessage("Failed to open WordPress Admin");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setWpLoading(false);
    }
  };

  const availableEnvironments = ["SANDBOX", "STAGING", "PRODUCTION"].filter(
    (env) => env !== site?.environment,
  );

  return (
    <Box>
      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Quick Actions
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Download Complete Site */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              disableElevation
              startIcon={
                downloadLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <CloudDownload />
                )
              }
              onClick={handleDownloadSite}
              disabled={downloadLoading || site?.status === "DELETED"}
              sx={{
                height: "100%",
                minHeight: 60,
                textTransform: "none",
                borderRadius: 2,
                justifyContent: "flex-start",
                px: 2,
              }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  Download Site
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete backup
                </Typography>
              </Box>
            </Button>
          </Grid>

          {/* Environment Migration */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              disableElevation
              startIcon={<SwapHoriz />}
              onClick={() => setMigrationOpen(true)}
              disabled={site?.status === "DELETED"}
              sx={{
                height: "100%",
                minHeight: 60,
                textTransform: "none",
                borderRadius: 2,
                justifyContent: "flex-start",
                px: 2,
              }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  Migrate Environment
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Current: {site?.environment}
                </Typography>
              </Box>
            </Button>
          </Grid>

          {/* PHPMyAdmin Access */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              disableElevation
              startIcon={
                pmaLoading ? <CircularProgress size={20} /> : <Storage />
              }
              onClick={handlePhpMyAdmin}
              disabled={pmaLoading || site?.status === "DELETED"}
              sx={{
                height: "100%",
                minHeight: 60,
                textTransform: "none",
                borderRadius: 2,
                justifyContent: "flex-start",
                px: 2,
              }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  PHPMyAdmin
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Database access
                </Typography>
              </Box>
            </Button>
          </Grid>

          {/* WordPress Admin Access */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              startIcon={
                wpLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <AdminPanelSettings />
                )
              }
              onClick={handleWpAdmin}
              disabled={wpLoading || site?.status !== "ACTIVE"}
              sx={{
                height: "100%",
                minHeight: 60,
                textTransform: "none",
                borderRadius: 2,
                justifyContent: "flex-start",
                px: 2,
              }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  WP Admin
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "primary.contrastText", opacity: 0.8 }}
                >
                  Auto-login
                </Typography>
              </Box>
            </Button>
          </Grid>

          {/* Visit Site */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              startIcon={<OpenInNew />}
              onClick={() =>
                window.open(
                  `${process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http"}://${site?.customDomain || site?.domain}/`,
                  "_blank",
                )
              }
              disabled={site?.status !== "ACTIVE"}
              sx={{
                height: "100%",
                minHeight: 60,
                textTransform: "none",
                borderRadius: 2,
                justifyContent: "flex-start",
                px: 2,
              }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  Visit Site
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "primary.contrastText", opacity: 0.8 }}
                >
                  Frontend view
                </Typography>
              </Box>
            </Button>
          </Grid>

          {/* Copy Info */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              disableElevation
              startIcon={<ContentCopy />}
              onClick={() => {
                const domain = site?.customDomain || site?.domain;
                const info = `Site: ${site?.name}\nDomain: ${domain}\nDB: ${site?.dbName}`;
                navigator.clipboard.writeText(info);
                setSuccessMessage("Site info copied to clipboard!");
                setTimeout(() => setSuccessMessage(""), 3000);
              }}
              sx={{
                height: "100%",
                minHeight: 60,
                textTransform: "none",
                borderRadius: 2,
                justifyContent: "flex-start",
                px: 2,
              }}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  Copy Details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Site information
                </Typography>
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Migration Dialog */}
      <Dialog
        open={migrationOpen}
        onClose={() => !migrateLoading && setMigrationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SwapHoriz color="primary" />
            <Typography variant="h6">Environment Migration</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Show progress when migrating */}
            {migrateLoading || migrationProgress ? (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Migrating: {site?.environment} → {targetEnvironment}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Please wait, this may take a few minutes for large sites.
                  </Typography>
                </Alert>

                {/* Progress bar */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {migrationProgress?.message || "Starting migration..."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {migrationProgress?.percent
                        ? `${migrationProgress.percent}%`
                        : ""}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant={
                      migrationProgress?.percent
                        ? "determinate"
                        : "indeterminate"
                    }
                    value={migrationProgress?.percent || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {/* Step indicators */}
                <Stepper
                  activeStep={MIGRATION_STEPS.findIndex(
                    (s) => s.key === migrationProgress?.step,
                  )}
                  orientation="vertical"
                  sx={{ "& .MuiStepLabel-label": { fontSize: "0.8rem" } }}
                >
                  {MIGRATION_STEPS.map((step) => (
                    <Step
                      key={step.key}
                      completed={
                        migrationProgress?.step === "completed" ||
                        MIGRATION_STEPS.findIndex(
                          (s) => s.key === migrationProgress?.step,
                        ) > MIGRATION_STEPS.findIndex((s) => s.key === step.key)
                      }
                    >
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            ) : (
              /* Show migration options when not migrating */
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Current Environment:</strong> {site?.environment}
                  </Typography>
                  <Typography variant="body2">
                    Choose how you want to migrate your site between
                    environments.
                  </Typography>
                </Alert>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Target Environment</InputLabel>
                  <Select
                    value={targetEnvironment}
                    label="Target Environment"
                    onChange={(e) => setTargetEnvironment(e.target.value)}
                  >
                    {availableEnvironments.map((env) => (
                      <MenuItem key={env} value={env}>
                        {env}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Migration:</strong> {site?.environment} →{" "}
                    {targetEnvironment || "Select target"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You can migrate between any environments freely.
                  </Typography>
                </Alert>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>What will happen:</strong>
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pre-migration backup will be created"
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="New isolated containers will be created in target environment"
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Files and database will be migrated"
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="WordPress URLs will be automatically updated"
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="You can rollback anytime if needed"
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                </List>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setMigrationOpen(false);
              setMigrationProgress(null);
            }}
            disabled={migrateLoading}
          >
            {migrateLoading ? "Close" : "Cancel"}
          </Button>
          {!migrateLoading && !migrationProgress && (
            <Button
              variant="contained"
              onClick={handleMigration}
              disabled={!targetEnvironment || migrateLoading}
              startIcon={<SwapHoriz />}
            >
              Start Migration
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Download Progress Indicator */}
      <Snackbar
        open={downloadLoading}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ width: "100%" }}
        >
          Downloading site... This may take a few minutes for large sites.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuickActionsPanel;
